/**
 * Route tree — lazy-loaded pages grouped by authentication state.
 *
 * Public routes (no AppShell):
 *   /landing      LandingPage
 *   /redeem       RedeemCodePage  (also reached after signup with no sub)
 *   /onboarding   OnboardingWizard
 *   /scenarios    ScenarioPickerPage
 *   /players      PlayerSelectPage
 *
 * Private routes (inside AppShell):
 *   /             Dashboard (currently DesignSystemShowcase)
 *   /marketplace, /portfolio, /banks, …  PagePlaceholder stubs
 */

import { lazy, Suspense, type ReactNode } from "react";
import { Link, Route, Routes } from "react-router-dom";
import { Button, EmptyState, Skeleton } from "@/components/ui";
import { Home } from "lucide-react";

// Lazy imports — one chunk per page
const LandingPage = lazy(() =>
  import("@/pages/LandingPage").then((m) => ({ default: m.LandingPage })),
);
const RedeemCodePage = lazy(() =>
  import("@/pages/RedeemCodePage").then((m) => ({ default: m.RedeemCodePage })),
);
const OnboardingWizard = lazy(() =>
  import("@/pages/OnboardingWizard").then((m) => ({ default: m.OnboardingWizard })),
);
const ScenarioPickerPage = lazy(() =>
  import("@/pages/ScenarioPickerPage").then((m) => ({
    default: m.ScenarioPickerPage,
  })),
);
const PlayerSelectPage = lazy(() =>
  import("@/pages/PlayerSelectPage").then((m) => ({ default: m.PlayerSelectPage })),
);
const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const MarketplacePage = lazy(() =>
  import("@/pages/MarketplacePage").then((m) => ({ default: m.MarketplacePage })),
);
const PortfolioPage = lazy(() =>
  import("@/pages/PortfolioPage").then((m) => ({ default: m.PortfolioPage })),
);
const BanksPage = lazy(() =>
  import("@/pages/BanksPage").then((m) => ({ default: m.BanksPage })),
);
const InsurancePage = lazy(() =>
  import("@/pages/InsurancePage").then((m) => ({ default: m.InsurancePage })),
);
const StocksPage = lazy(() =>
  import("@/pages/StocksPage").then((m) => ({ default: m.StocksPage })),
);
const AccountingPage = lazy(() =>
  import("@/pages/AccountingPage").then((m) => ({ default: m.AccountingPage })),
);
const EventsPage = lazy(() =>
  import("@/pages/EventsPage").then((m) => ({ default: m.EventsPage })),
);
const CalculatorPage = lazy(() =>
  import("@/pages/CalculatorPage").then((m) => ({ default: m.CalculatorPage })),
);
const AchievementsPage = lazy(() =>
  import("@/pages/AchievementsPage").then((m) => ({ default: m.AchievementsPage })),
);
const AcademyPage = lazy(() =>
  import("@/pages/AcademyPage").then((m) => ({ default: m.AcademyPage })),
);
const ChallengesPage = lazy(() =>
  import("@/pages/ChallengesPage").then((m) => ({ default: m.ChallengesPage })),
);
const LeaderboardPage = lazy(() =>
  import("@/pages/LeaderboardPage").then((m) => ({ default: m.LeaderboardPage })),
);
const GlossaryPage = lazy(() =>
  import("@/pages/GlossaryPage").then((m) => ({ default: m.GlossaryPage })),
);
const AnalyticsPage = lazy(() =>
  import("@/pages/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })),
);
const AdminPage = lazy(() =>
  import("@/pages/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const DesignSystemShowcase = lazy(() =>
  import("@/pages/DesignSystemShowcase").then((m) => ({ default: m.DesignSystemShowcase })),
);
const PagePlaceholder = lazy(() => import("@/pages/PagePlaceholder"));

function PageFallback() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function Page({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

/** Slots that render as PagePlaceholder (remaining — none, all done). */
const PLACEHOLDER_SLOTS: readonly string[] = [];

/** Routes rendered WITHOUT AppShell (full-page onboarding / auth flow). */
export const PUBLIC_ROUTE_PATHS = [
  "/landing",
  "/redeem",
  "/onboarding",
  "/scenarios",
  "/players",
] as const;

export function AppRoutes() {
  return (
    <Routes>
      {/* Public / onboarding — no AppShell */}
      <Route path="/landing" element={<Page><LandingPage /></Page>} />
      <Route path="/redeem" element={<Page><RedeemCodePage /></Page>} />
      <Route path="/onboarding" element={<Page><OnboardingWizard /></Page>} />
      <Route path="/scenarios" element={<Page><ScenarioPickerPage /></Page>} />
      <Route path="/players" element={<Page><PlayerSelectPage /></Page>} />

      {/* Private app */}
      <Route path="/" element={<Page><DashboardPage /></Page>} />
      <Route path="/marketplace" element={<Page><MarketplacePage /></Page>} />
      <Route path="/portfolio" element={<Page><PortfolioPage /></Page>} />
      <Route path="/banks" element={<Page><BanksPage /></Page>} />
      <Route path="/insurance" element={<Page><InsurancePage /></Page>} />
      <Route path="/stocks" element={<Page><StocksPage /></Page>} />
      <Route path="/accounting" element={<Page><AccountingPage /></Page>} />
      <Route path="/events" element={<Page><EventsPage /></Page>} />
      <Route path="/calculator" element={<Page><CalculatorPage /></Page>} />
      <Route path="/achievements" element={<Page><AchievementsPage /></Page>} />
      <Route path="/academy" element={<Page><AcademyPage /></Page>} />
      <Route path="/challenges" element={<Page><ChallengesPage /></Page>} />
      <Route path="/leaderboard" element={<Page><LeaderboardPage /></Page>} />
      <Route path="/glossary" element={<Page><GlossaryPage /></Page>} />
      <Route path="/analytics" element={<Page><AnalyticsPage /></Page>} />
      <Route path="/admin" element={<Page><AdminPage /></Page>} />
      <Route path="/design-system" element={<Page><DesignSystemShowcase /></Page>} />
      {PLACEHOLDER_SLOTS.map((p) => (
        <Route
          key={p}
          path={`/${p}`}
          element={<Page><PagePlaceholder slug={p} /></Page>}
        />
      ))}

      <Route
        path="*"
        element={
          <Page>
            <EmptyState
              className="py-24"
              title="Stránka nenalezena"
              description="Adresa, na kterou jsi zamířil/a, neexistuje. Zkontroluj odkaz, nebo se vrať na Dashboard."
              action={
                <Link to="/">
                  <Button variant="primary">
                    <Home size={14} /> Zpět na Dashboard
                  </Button>
                </Link>
              }
            />
          </Page>
        }
      />
    </Routes>
  );
}
